import React, { useState } from "react";
import { BackHand as BackHandIcon } from "@mui/icons-material";
import { BackHandOutlined as BackHandOutlinedIcon } from "@mui/icons-material";
import { Check as CheckIcon } from "@mui/icons-material";
import { ThumbUp as ThumbUpIcon } from "@mui/icons-material";
import { Settings as SettingsIcon } from "@mui/icons-material";
import { ExpandMore as ExpandMoreIcon } from "@mui/icons-material";
import { ExpandLess as ExpandLessIcon } from "@mui/icons-material";
import { TipsAndUpdates as TipsAndUpdatesIcon } from "@mui/icons-material";

export const Checkpoint = (props) => {
  const { block, editor } = props;
  const { checkpointSelected } = block.props;

  // Config from block props - use checkpointSelected if available, otherwise defaults
  const blockData = checkpointSelected || {};
  const [config, setConfig] = useState({
    title: blockData.name || block.props.title || "Manager Approval Required",
    save_to_profile: blockData.save_to_profile !== undefined ? blockData.save_to_profile : (block.props.save_to_profile !== undefined ? block.props.save_to_profile : false),
  });

  const [isChecked, setIsChecked] = React.useState(
    checkpointSelected?.user_has_passed
  );

  // Open by default in edit mode so it's obvious the block is configurable
  // (only rendered there; complete mode never shows the panel).
  const [showSettings, setShowSettings] = useState(true);

  const updateConfig = (newConfig) => {
    setConfig(newConfig);
    // Update the block props in the editor
    editor.updateBlock(block, {
      type: "checkpoint",
      props: {
        ...block.props,
        ...newConfig,
      },
    });
  };

  const t = (english, spanish, chinese) => {
    if (props.language === "chinese") return chinese;
    if (props.language === "spanish") return spanish;
    return english;
  };

  const getDefaultStopMessage = () => {
    return t(
      "This is a checkpoint. Your manager must sign off by checking this box before you can continue.",
      "Este es un punto de control. Tu gerente debe aprobar marcando esta casilla antes de que puedas continuar.",
      "这是一个检查点。在你继续之前，你的经理必须勾选此框进行批准。"
    );
  };

  const getDefaultGoMessage = () => {
    return t(
      "Congratulations, your manager has marked this checkpoint complete. You may continue!",
      "Felicidades, tu gerente ha marcado este punto de control como completo. ¡Puedes continuar!",
      "恭喜，你的经理已将此检查点标记为完成。你可以继续了！"
    );
  };

  // Complete mode - Shows the actual checkpoint interface
  if (props.mode === "complete") {
    // isChecked comes from checkpointSelected.user_has_passed — the viewing
    // user's own approval (per user+section). In the track/module preview
    // (isPreview) that's the admin's state, so show the neutral STOP gate and
    // disable the check-off there; the real GO state + sign-off is learner-only.
    const isPreview = props.isPreview === true;
    const showChecked = isChecked && !isPreview;
    return (
      <>
        {!showChecked ? (
          <div
            className="w-full bg-rose-200 py-4 px-5 rounded-lg flex items-center space-x-5"
            contentEditable={false}
          >
            <div className="text-rose-700">
              <BackHandIcon style={{ fontSize: "48px" }} />
            </div>
            <div className="flex-1">
              <p className="text-rose-700 font-bold" style={{ fontSize: "24px" }}>
                {t("STOP", "ALTO", "停止")}
              </p>
              <p className="text-rose-700 font-semibold" style={{ fontSize: "18px" }}>
                {config.title}
              </p>
              <p className="text-rose-700" style={{ fontSize: "16px" }}>
                {getDefaultStopMessage()}
              </p>
            </div>
            <div
              className={`w-10 h-10 border border-slate-200 rounded bg-white ${
                props.userType !== "Employee" && !isPreview
                  ? "hover:bg-slate-50 cursor-pointer"
                  : ""
              }`}
              onClick={() => {
                // Never mutate completion from the preview — it isn't a real
                // sign-off and would POST against the admin's own record.
                if (props.userType !== "Employee" && !isPreview) {
                  props.post_learning_block_user_passed(
                    checkpointSelected.uuid,
                    true,
                    props.employeeID
                  );
                  setIsChecked(true);
                }
              }}
            ></div>
          </div>
        ) : (
          <div
            className="w-full bg-emerald-200 py-4 px-5 rounded-lg flex items-center space-x-5"
            contentEditable={false}
          >
            <div className="text-emerald-700">
              <ThumbUpIcon style={{ fontSize: "48px" }} />
            </div>
            <div className="flex-1">
              <p
                className="text-emerald-700 font-bold"
                style={{ fontSize: "24px" }}
              >
                {t("GO", "SIGUE", "继续")}
              </p>
              <p className="text-emerald-700 font-semibold" style={{ fontSize: "18px" }}>
                {config.title}
              </p>
              <p className="text-emerald-700" style={{ fontSize: "16px" }}>
                {getDefaultGoMessage()}
              </p>
            </div>
            <div className="w-10 h-10 border border-white rounded flex items-center justify-center bg-emerald-600 text-white">
              <CheckIcon style={{ fontSize: "26px" }} />
            </div>
          </div>
        )}
      </>
    );
  }

  // Edit mode - Shows configuration interface (manager side)
  return (
    <div
      className="w-full border-2 border-gray-200 rounded-lg bg-gray-50 outline-none focus:outline-none"
      contentEditable={false}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200 rounded-t-lg">
        <div className="flex items-center gap-2">
          <BackHandOutlinedIcon className="text-violet-700" style={{ fontSize: 24 }} />
          <div>
            <div className="text-sm font-semibold text-gray-700">
              {t("Manager Checkpoint", "Punto de control del gerente", "经理检查点")}
            </div>
            <div className="text-xs text-gray-500">
              {t("Requires manager approval to continue", "Requiere aprobación del gerente para continuar", "需要经理批准后才能继续")}
            </div>
          </div>
        </div>

        {/* Settings Toggle Button */}
        <button
          className="cursor-pointer hover:bg-gray-200 p-2 rounded transition-colors flex items-center gap-1"
          onClick={() => setShowSettings(!showSettings)}
          type="button"
        >
          <SettingsIcon className="text-violet-700" style={{ fontSize: 20 }} />
          {showSettings ? (
            <ExpandLessIcon className="text-violet-700" style={{ fontSize: 18 }} />
          ) : (
            <ExpandMoreIcon className="text-violet-700" style={{ fontSize: 18 }} />
          )}
        </button>
      </div>

      {/* Custom Settings Panel */}
      {showSettings && (
        <div className="px-4 py-4 bg-white border-b border-gray-200">
          <h4 className="text-sm font-semibold text-gray-900 mb-4">
            {t("Configure Checkpoint", "Configurar punto de control", "配置检查点")}
          </h4>

          {/* Title Input */}
          <div className="mb-4">
            <label className="block text-xs font-medium text-gray-700 mb-1.5 mt-1">
              {t("Title", "Título", "标题")}
            </label>
            <input
              type="text"
              placeholder={t("e.g., Safety Training Approval", "p. ej., Aprobación de capacitación de seguridad", "例如：安全培训批准")}
              value={config.title}
              onChange={(e) => updateConfig({ ...config, title: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-700 focus:border-transparent"
            />
          </div>

          <div className="border-t border-gray-200 my-4"></div>

          {/* Save to employee profile switch */}
          <div className="mb-0">
            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <div className="text-sm font-medium text-gray-900">
                  {t(
                    "Keep a copy on the employee's profile",
                    "Guardar una copia en el perfil del empleado",
                    "在员工档案中保留副本"
                  )}
                </div>
                <div className="text-xs text-gray-500">
                  {t(
                    "Stays on their profile even after the training changes or is unassigned",
                    "Permanece en su perfil incluso si la capacitación cambia o se desasigna",
                    "即使培训内容更改或取消分配，仍会保留在其档案中"
                  )}
                </div>
              </div>
              <div className="relative">
                <input
                  type="checkbox"
                  checked={config.save_to_profile}
                  onChange={(e) => updateConfig({ ...config, save_to_profile: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-violet-700"></div>
              </div>
            </label>
          </div>
        </div>
      )}

      {/* Preview of what employees will see */}
      <div className="px-4 py-4">
        <div className="bg-rose-200 rounded-lg py-4 px-5">
          <div className="flex items-center space-x-5">
            <div className="text-rose-700">
              <BackHandIcon style={{ fontSize: "48px" }} />
            </div>
            <div className="flex-1">
              <p className="text-rose-700 font-bold" style={{ fontSize: "24px" }}>
                {t("STOP", "ALTO", "停止")}
              </p>
              <p className="text-rose-700 font-semibold" style={{ fontSize: "18px" }}>
                {config.title}
              </p>
              <p className="text-rose-700" style={{ fontSize: "16px" }}>
                {getDefaultStopMessage()}
              </p>
              <p className="text-rose-700" style={{ fontSize: '12px', marginTop: '12px' }}>
                {t(
                  "(Employee checkpoint interface - preview only)",
                  "(Interfaz de punto de control para empleados: solo vista previa)",
                  "（员工检查点界面，仅供预览）"
                )}
              </p>
            </div>
            <div className="w-10 h-10 border border-slate-200 rounded bg-white"></div>
          </div>
        </div>
      </div>

      {/* Footer help text */}
      <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 rounded-b-lg">
        <div className="flex items-center gap-1.5 text-xs text-gray-600">
          <TipsAndUpdatesIcon style={{ fontSize: 16 }} />
          <span>
            {t("Click the settings icon to configure this checkpoint", "Haz clic en el ícono de configuración para configurar este punto de control", "点击设置图标来配置此检查点")}
          </span>
        </div>
      </div>
    </div>
  );
};
